import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import backendApi from "@/api/backendApi";

import ProfileHeader from "../components/profile/ProfileHeader";
import ProfileQuickLinks from "../components/profile/ProfileQuickLinks";
import ResumeSection from "../components/profile/ResumeSection";
import KeySkillsSection from "../components/profile/KeySkillsSection";
import EmploymentSection from "../components/profile/EmploymentSection";
import EducationSection from "../components/profile/EducationSection";
import ITSkillsSection from "../components/profile/ITSkillsSection";
import ProjectsSection from "../components/profile/ProjectsSection";
import ProfileSummarySection from "../components/profile/ProfileSummarySection";
import AccomplishmentsSection from "../components/profile/AccomplishmentsSection";
import CareerProfileSection from "../components/profile/CareerProfileSection";
import PersonalDetailsSection from "../components/profile/PersonalDetailsSection";
import ResumeHeadlineSection from "../components/profile/ResumeHeadlineSection";

import { Button } from "@/components/ui/button";
import { Crown } from "lucide-react";

import { useAuth } from "../../context/AuthContext";

export default function Profile() {
  const { user } = useAuth();
  const [activeSection, setActiveSection] = useState("resume");


  // ✅ FIX: Profile query maps completion fields properly so ProfileHeader
  // can show "Profile complete" or the correct missing count
  const { data: profile = {}, isLoading: loadingProfile } = useQuery({
    queryKey: ["userProfile"],
    queryFn: async () => {
      const res = await backendApi.entities.UserProfile.getMe();
      if (res?.user) {
        return {
          ...res.user,
          profile_completion: res.completionPercentage ?? 0,
          missingFields: res.missingFields || [],
          missingLabels: res.missingLabels || [],
          filledSections: res.filledSections || 0,
          totalSections: res.totalSections || 10,
          isComplete: res.isComplete || false,
        };
      }
      // Backend may return the profile directly (not nested under .user)
      return {
        ...res,
        profile_completion: res?.completionPercentage ?? res?.profile_completion ?? 0,
        missingFields: res?.missingFields || [],
        missingLabels: res?.missingLabels || [],
        isComplete: res?.isComplete || false,
      };
    },
    enabled: !!user,
    staleTime: 30000,
    // Only log the error, ProtectedRoute handles the redirect if needed
    onError: (err) => {
      console.error("Profile fetch failed:", err?.message || err);
    },
  });

  // ✅ FIX: Observer registers only once + clears timeout on unmount
  useEffect(() => {
    if (loadingProfile) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: "-100px 0px -60% 0px" }
    );

    const sections = [
      "resume", "headline", "skills", "employment", "education",
      "itskills", "projects", "summary", "accomplishments", "career", "personal",
    ];

    const timeoutId = setTimeout(() => {
      sections.forEach((id) => {
        const el = document.getElementById(id);
        if (el) observer.observe(el);
      });
    }, 500);

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [loadingProfile]);

  if (loadingProfile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading your profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="w-full px-4 sm:px-6 py-6">
        <div className="flex gap-6">

          <div className="w-64 hidden lg:block">
            <ProfileQuickLinks
              activeSection={activeSection}
              onSectionChange={setActiveSection}
              profile={profile}
            />
          </div>

          <div className="flex-1 space-y-6">

            <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4 rounded-xl flex items-center justify-between shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-400 rounded-lg flex items-center justify-center">
                  <Crown className="w-6 h-6 text-yellow-800" />
                </div>
                <p className="text-white font-medium">
                  Elevate your profile with AI-powered enhancements
                </p>
              </div>
              <Button className="bg-white text-black rounded-full hover:bg-gray-100">
                Upgrade now
              </Button>
            </div>

            <div id="header">
              <ProfileHeader user={user} profile={profile} />
            </div>

            <div id="resume"><ResumeSection profile={profile} /></div>
            <div id="headline"><ResumeHeadlineSection profile={profile} /></div>
            <div id="skills"><KeySkillsSection profile={profile} /></div>
            <div id="employment"><EmploymentSection profile={profile} /></div>
            <div id="education"><EducationSection profile={profile} /></div>
            <div id="itskills"><ITSkillsSection profile={profile} /></div>
            <div id="projects"><ProjectsSection profile={profile} /></div>
            <div id="summary"><ProfileSummarySection profile={profile} /></div>
            <div id="accomplishments"><AccomplishmentsSection profile={profile} /></div>
            <div id="career"><CareerProfileSection profile={profile} /></div>
            <div id="personal"><PersonalDetailsSection profile={profile} /></div>

          </div>
        </div>
      </div>
    </div>
  );
}